import { useCallback, useContext, useMemo } from "react";
import { useToast } from "../../components/ui/toast-context-provider";
import {
  useGetAppSettingsQuery,
  useSetAppSettingsMutation,
} from "../../queries/appsettings.query";
import * as Yup from "yup";
import { AppSettings } from "../../queries/models/appsettings.model";
import { useQueryClient } from "react-query";
import { Form, Formik } from "formik";
import { EditContentHeader } from "../../components/ui/edit-content-header";
import { classNames } from "primereact/utils";
import { Dropdown } from "primereact/dropdown";
import FormikInputText from "../../components/ui/formik/FormikInputText";
import FormikInputSwitch from "../../components/ui/formik/FormikInputSwitch";
import { PrimeReactContext } from "primereact/api";
import FormikImageFileUpload from "../../components/ui/formik/FormikFilesUpload";

export function SettingsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const appSettingsQuery = useGetAppSettingsQuery();
  const setAppSettingsMutation = useSetAppSettingsMutation();
  const { changeTheme } = useContext(PrimeReactContext);
  const hasWriteAccess = true;

  const config = useMemo(() => {
    return appSettingsQuery.data;
  }, [appSettingsQuery.data]);

  const initialValues: AppSettings = {
    appName: config?.appName ?? "",
    usePlanShortcuts: config?.usePlanShortcuts ?? false,
    showPlanUser: config?.showPlanUser ?? false,
    adminAsUser: config?.adminAsUser ?? false,
    prepaidCardCount: config?.prepaidCardCount ?? 5,
    exhaustedHoursInDay: config?.exhaustedHoursInDay ?? 4,
    exhaustedHoursInRow: config?.exhaustedHoursInRow ?? 3,
    shouldChargeForCancelledRides:
      config?.shouldChargeForCancelledRides ?? false,
    theme: config?.theme ?? "bootstrap4-dark-blue",
  };

  const validationSchema = Yup.object({
    appName: Yup.string().required("Required"),
    usePlanShortcuts: Yup.boolean().required("Required"),
    showPlanUser: Yup.boolean().required("Required"),
    adminAsUser: Yup.boolean().required("Required"),
    shouldChargeForCancelledRides: Yup.boolean().required("Required"),
    exhaustedHoursInDay: Yup.number().required("Required"),
    exhaustedHoursInRow: Yup.number().required("Required"),
    prepaidCardCount: Yup.number()
      .required("Required")
      .min(1, "Must be greater than 0")
      .max(100, "Must be less than 100"),
    theme: Yup.string(),
  });

  const themeOptions = [
    { name: "Bootstrap Light Blue", value: "bootstrap4-light-blue" },
    { name: "Bootstrap Light Purple", value: "bootstrap4-light-purple" },
    { name: "Bootstrap Dark Blue", value: "bootstrap4-dark-blue" },
    { name: "Bootstrap Dark Purple", value: "bootstrap4-dark-purple" },
    { name: "Material Light Indigo", value: "md-light-indigo" },
    { name: "Material Light Deep Purple", value: "md-light-deeppurple" },
    { name: "Material Dark Indigo", value: "md-dark-indigo" },
    { name: "Material Dark Deep Purple", value: "md-dark-deeppurple" },
    { name: "Material Compact Light Indigo", value: "mdc-light-indigo" },
    {
      name: "Material Compact Light Deep Purple",
      value: "mdc-light-deeppurple",
    },
    { name: "Material Compact Dark Indigo", value: "mdc-dark-indigo" },
    { name: "Material Compact Dark Deep Purple", value: "mdc-dark-deeppurple" },
    { name: "Tailwind Light", value: "tailwind-light" },
    { name: "Fluent Light", value: "fluent-light" },
    { name: "Lara Light Blue", value: "lara-light-blue" },
    { name: "Lara Light Indigo", value: "lara-light-indigo" },
    { name: "Lara Light Purple", value: "lara-light-purple" },
    { name: "Lara Light Teal", value: "lara-light-teal" },
    { name: "Lara Dark Blue", value: "lara-dark-blue" },
    { name: "Lara Dark Indigo", value: "lara-dark-indigo" },
    { name: "Lara Dark Purple", value: "lara-dark-purple" },
    { name: "Lara Dark Teal", value: "lara-dark-teal" },
    { name: "Soho Light", value: "soho-light" },
    { name: "Soho Dark", value: "soho-dark" },
    { name: "Viva Light", value: "viva-light" },
    { name: "Viva Dark", value: "viva-dark" },
    { name: "Mira", value: "mira" },
    { name: "Nano", value: "nano" },
    { name: "Saga Blue", value: "saga-blue" },
    { name: "Saga Green", value: "saga-green" },
    { name: "Saga Orange", value: "saga-orange" },
    { name: "Saga Purple", value: "saga-purple" },
    { name: "Vela Blue", value: "vela-blue" },
    { name: "Vela Green", value: "vela-green" },
    { name: "Vela Orange", value: "vela-orange" },
    { name: "Vela Purple", value: "vela-purple" },
    { name: "Arya Blue", value: "arya-blue" },
    { name: "Arya Green", value: "arya-green" },
    { name: "Arya Orange", value: "arya-orange" },
    { name: "Arya Purple", value: "arya-purple" },
  ];

  const onSubmit = useCallback(
    async (values: AppSettings) => {
      try {
        await setAppSettingsMutation.mutateAsync(values, {
          onSuccess: async () => {
            await queryClient.invalidateQueries("appsettings");
            toast.current?.show({
              severity: "success",
              detail: "Zapisano!",
            });
          },
        });
      } catch (error) {
        console.error("Submission error:", error);
      }
    },
    [setAppSettingsMutation, queryClient, toast]
  );

  return (
    <div className="p-3 h-full w-full">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange
        validateOnMount
      >
        {(formik) => (
          <Form
            autoComplete="off"
            className="flex flex-column h-full w-full overflow-hidden"
          >
            <div className="mb-2 overflow-y-auto overflow-x-hidden h-full">
              <div className="col-12">
                <FormikInputText
                  label="Nazwa firmy"
                  name="appName"
                  validationSchema={validationSchema}
                  isIndependent
                  disabled={!hasWriteAccess}
                />
              </div>

              <div className="col-12">
                <label htmlFor="theme" className="block mb-2">
                  Motyw graficzny
                </label>
                <Dropdown
                  id="theme"
                  name="theme"
                  value={formik.values.theme}
                  options={themeOptions}
                  onChange={(e) => {
                    formik.setFieldValue("theme", e.value);

                    // Optional: apply immediately
                    const currentHref = document
                      .getElementById("theme-css")
                      ?.getAttribute("href");
                    const currentTheme =
                      currentHref?.split("/")[2] || "bootstrap4-dark-blue";

                    if (changeTheme) {
                      changeTheme(currentTheme, e.value, "theme-css", () => {
                        console.log("Theme applied");
                      });
                    }
                  }}
                  optionLabel="name"
                  placeholder="Wybierz motyw"
                  disabled={!hasWriteAccess}
                  className={classNames({
                    "w-full": true,
                    "p-invalid": formik.touched.theme && !!formik.errors.theme,
                  })}
                />
              </div>
              <div className="col-12">
                <FormikInputText
                  label="Ilość kredytów w karnecie"
                  name="prepaidCardCount"
                  validationSchema={validationSchema}
                  isIndependent
                  disabled={!hasWriteAccess}
                />
              </div>
              <div className="col-12">
                <FormikInputText
                  label="Godziny pod rząd (niebieska czcionka)"
                  name="exhaustedHoursInRow"
                  validationSchema={validationSchema}
                  isIndependent
                  disabled={!hasWriteAccess}
                />
              </div>
              <div className="col-12">
                <FormikInputText
                  label="Godziny w ciągu dnia (niebieska czcionka)"
                  name="exhaustedHoursInDay"
                  validationSchema={validationSchema}
                  isIndependent
                  disabled={!hasWriteAccess}
                />
              </div>
              <div className="col-12">
                <FormikInputSwitch
                  label="Odwołane jazdy są płatne"
                  name="shouldChargeForCancelledRides"
                  validationSchema={validationSchema}
                  isIndependent
                  disabled={!hasWriteAccess}
                  checked={false}
                />
              </div>
            </div>

            <EditContentHeader
              header={"Ustawienia użytkownika"}
              showDeleteButton={false}
              showCancelButton={false}
              saveButtonDisabled={
                !hasWriteAccess || !formik.dirty || !formik.isValid
              }
              onSaveClick={async () => {
                if (!formik.isValid) {
                  toast.current?.show({
                    severity: "error",
                    detail: "Formularz zawiera błędy.",
                  });
                  return;
                }
                return formik.submitForm();
              }}
              onCancelClick={() => {}}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}
